/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from "@angular/common/http";
import { Observable, EMPTY, of } from "rxjs";
import { switchMap, catchError } from "rxjs/operators";
import { AccountInfo, AuthenticationResult, BrowserConfigurationAuthError, InteractionType } from "@azure/msal-browser";
import { Injectable, Inject } from "@angular/core";
import { MsalInterceptorAuthRequest, MsalInterceptorConfiguration, MsalService, MSAL_INTERCEPTOR_CONFIG } from "@azure/msal-angular";
import { b2cPolicies } from "../../auth.config";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    constructor(
        @Inject(MSAL_INTERCEPTOR_CONFIG) private msalInterceptorConfig: MsalInterceptorConfiguration,
        private authService: MsalService
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.msalInterceptorConfig.interactionType !== InteractionType.Popup && this.msalInterceptorConfig.interactionType !== InteractionType.Redirect) {
            throw new BrowserConfigurationAuthError(
                "invalid_interaction_type",
                "Invalid interaction type provided to MSAL Interceptor. InteractionType.Popup, InteractionType.Redirect must be provided in the msalInterceptorConfiguration"
            );
        }

        // Sets account as active account or first account
        let account: AccountInfo;
        if (!!this.authService.instance.getActiveAccount()) {
            this.authService.getLogger().verbose("Interceptor - active account selected");
            account = this.authService.instance.getActiveAccount();
        } else {
            this.authService.getLogger().verbose("Interceptor - no active account, fallback to first account");
            account = this.authService.instance.getAllAccounts()[0];
        }

        if (account != null) {
            const authRequest =
                typeof this.msalInterceptorConfig.authRequest === "function"
                    ? this.msalInterceptorConfig.authRequest(this.authService, req, { account: account })
                    : { ...this.msalInterceptorConfig.authRequest, account };

            // Note: For MSA accounts, include openid scope when calling acquireTokenSilent to return idToken
            return this.authService.acquireTokenSilent({ ...authRequest, scopes: b2cPolicies.apiScopes, account }).pipe(
                catchError(() => {
                    this.authService.getLogger().error("Interceptor - acquireTokenSilent rejected with error. Invoking interaction to resolve.");
                    return this.acquireTokenInteractively(authRequest, b2cPolicies.apiScopes);
                }),
                switchMap((result: AuthenticationResult) => {
                    if (!result.accessToken) {
                        this.authService
                            .getLogger()
                            .error(
                                "Interceptor - acquireTokenSilent resolved with null access token. Known issue with B2C tenants, invoking interaction to resolve."
                            );
                        return this.acquireTokenInteractively(authRequest, b2cPolicies.apiScopes);
                    }
                    return of(result);
                }),
                switchMap((result: AuthenticationResult) => {
                    this.authService.getLogger().verbose("Interceptor - setting authorization headers");
                    const headers = req.headers.set("Authorization", `Bearer ${result.accessToken}`);

                    const requestClone = req.clone({ headers });
                    return next.handle(requestClone);
                })
            );
        } else {
            return next.handle(req);
        }
    }

    /**
     * Invoke interaction for the given set of scopes
     * @param authRequest Request
     * @param scopes Array of scopes for the request
     * @returns Result from the interactive request
     */
    private acquireTokenInteractively(authRequest: MsalInterceptorAuthRequest, scopes: string[]): Observable<AuthenticationResult> {
        if (this.msalInterceptorConfig.interactionType === InteractionType.Popup) {
            this.authService.getLogger().verbose("Interceptor - error acquiring token silently, acquiring by popup");
            return this.authService.acquireTokenPopup({ ...authRequest, scopes });
        }
        this.authService.getLogger().verbose("Interceptor - error acquiring token silently, acquiring by redirect");
        const redirectStartPage = window.location.href;
        this.authService.acquireTokenRedirect({ ...authRequest, scopes, redirectStartPage });
        return EMPTY;
    }
}
